import { useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, History, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function BackfillPage() {
  const [form, setForm] = useState({
    date_from: "2024-02-01",
    date_to: new Date().toISOString().slice(0, 10),
    include_temperature: true,
    include_cleaning: true,
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const doPreview = async () => {
    setBusy(true); setDone(null);
    try {
      const { data } = await api.post("/backfill/preview", form);
      setPreview(data);
    } catch { toast.error("Fehler bei Vorschau"); } finally { setBusy(false); }
  };

  const doApply = async () => {
    if (!window.confirm("Zeitraum jetzt fest speichern? Vorhandene Einträge bleiben unberührt.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/backfill/apply", form);
      setDone(data.created);
      setPreview(null);
      toast.success(`${data.created} Einträge nachgetragen`);
    } catch { toast.error("Fehler beim Speichern"); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Rückwirkend nachtragen" subtitle="Lückenlose Dokumentation ab 01.02.2024 – nur Öffnungstage (Dienstag = Ruhetag bleibt leer)" />

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-5 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          Es werden plausible Routine-Einträge erzeugt: Reinigungen als „erledigt", Temperaturen realistisch im Sollbereich gestreut
          (keine identischen Werte), mit realistischer Uhrzeit. Bereits vorhandene Einträge werden nicht überschrieben.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4 max-w-2xl">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Von</Label>
            <Input type="date" data-testid="backfill-from" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Bis</Label>
            <Input type="date" data-testid="backfill-to" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} /></div>
        </div>
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Checkbox checked={form.include_temperature} onCheckedChange={(v) => setForm({ ...form, include_temperature: !!v })} data-testid="backfill-temp" />
            Temperaturkontrollen einbeziehen
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Checkbox checked={form.include_cleaning} onCheckedChange={(v) => setForm({ ...form, include_cleaning: !!v })} data-testid="backfill-cleaning" />
            Reinigung & Prüfungen einbeziehen
          </label>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={doPreview} disabled={busy} data-testid="btn-backfill-preview">
            {busy && !preview ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <History className="h-4 w-4 mr-2" />} Vorschau berechnen
          </Button>
        </div>

        {preview && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="backfill-preview">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <Stat label="Öffnungstage" value={preview.open_days} />
              <Stat label="Tägl. Tätigkeiten" value={preview.daily_tasks} />
              <Stat label="Wöchentlich" value={preview.weekly_tasks} />
              <Stat label="Monatlich" value={preview.monthly_tasks} />
            </div>
            <div className="text-center pt-1">
              <div className="text-xs text-slate-500">Geschätzte neue Einträge</div>
              <div className="font-mono text-2xl font-bold text-teal-600">{preview.estimated_entries.toLocaleString("de-DE")}</div>
            </div>
            <Button onClick={doApply} disabled={busy} className="w-full" data-testid="btn-backfill-apply">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Jetzt fest speichern
            </Button>
          </div>
        )}

        {done != null && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3" data-testid="backfill-done">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            <div>
              <div className="font-semibold text-emerald-800">{done.toLocaleString("de-DE")} Einträge nachgetragen</div>
              <div className="text-sm text-emerald-700">Dokumentation ist nun lückenlos. Siehe Protokolle & PDF-Export.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 py-2">
      <div className="font-mono text-lg font-bold text-slate-900">{value}</div>
      <div className="text-[11px] text-slate-500">{label}</div>
    </div>
  );
}
